import { Button } from "@aurumiq/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@aurumiq/ui/components/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SelectField, TextAreaField, TextField } from "@/components/field";
import type { Release } from "@/lib/types";
import { useCreateRelease, usePatchRelease } from "./queries";

/** SemVer 2.0 (with optional pre-release / build metadata). */
export const SEMVER_RE =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

const draftSchema = z.object({
  channel: z.enum(["stable", "beta"]),
  version: z
    .string()
    .trim()
    .regex(SEMVER_RE, "Must be a semantic version, e.g. 1.5.0 or 1.5.0-beta.1"),
  notes_md: z.string().max(20_000),
});
type DraftValues = z.infer<typeof draftSchema>;

export function DraftReleaseDialog({
  open,
  onOpenChange,
  channel,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: "stable" | "beta";
  onCreated?: (release: Release) => void;
}) {
  const create = useCreateRelease();
  const form = useForm<DraftValues>({
    resolver: zodResolver(draftSchema),
    values: { channel, version: "", notes_md: "" },
  });
  const err = (k: keyof DraftValues) => form.formState.errors[k]?.message;
  const onSubmit = form.handleSubmit(async (v) => {
    const r = await create.mutateAsync(v);
    form.reset();
    onOpenChange(false);
    onCreated?.(r);
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Draft release</DialogTitle>
            <DialogDescription>
              Creates an unpublished release. CI (or the upload script) attaches artifacts; publish
              once every artifact shows as uploaded.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField label="Channel" error={err("channel")} {...form.register("channel")}>
              <option value="stable">stable</option>
              <option value="beta">beta</option>
            </SelectField>
            <TextField
              label="Version"
              placeholder="1.5.0"
              error={err("version")}
              autoFocus
              {...form.register("version")}
            />
          </div>
          <TextAreaField
            label="Release notes (markdown)"
            rows={8}
            placeholder={"## What's new\n\n- …"}
            error={err("notes_md")}
            {...form.register("notes_md")}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create draft"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const editSchema = z.object({
  notes_md: z.string().max(20_000),
  staging_pct: z.coerce.number().int().min(0, "0–100").max(100, "0–100"),
  reason: z.string().trim(),
});
type EditValues = z.input<typeof editSchema>;
type EditParsed = z.output<typeof editSchema>;

export function EditReleaseDialog({
  release,
  onOpenChange,
}: {
  release: Release | null;
  onOpenChange: (open: boolean) => void;
}) {
  const patch = usePatchRelease();
  const form = useForm<EditValues, unknown, EditParsed>({
    resolver: zodResolver(editSchema),
    values: {
      notes_md: release?.notes_md ?? "",
      staging_pct: release?.staging_pct ?? 100,
      reason: "",
    },
  });
  const err = (k: keyof EditValues) => form.formState.errors[k]?.message;
  const onSubmit = form.handleSubmit(async (v) => {
    if (!release) return;
    await patch.mutateAsync({
      id: release.id,
      notes_md: v.notes_md,
      staging_pct: v.staging_pct,
      reason: v.reason || undefined,
    });
    onOpenChange(false);
  });
  return (
    <Dialog open={release !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Edit {release?.version}</DialogTitle>
            <DialogDescription>
              Staging percentage controls how many devices see this update; 100 = everyone.
            </DialogDescription>
          </DialogHeader>
          <TextField
            label="Staging %"
            type="number"
            min={0}
            max={100}
            error={err("staging_pct")}
            {...form.register("staging_pct")}
          />
          <TextAreaField
            label="Release notes (markdown)"
            rows={8}
            error={err("notes_md")}
            {...form.register("notes_md")}
          />
          <TextAreaField
            label="Reason (optional)"
            placeholder="Recorded in the audit log."
            {...form.register("reason")}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={patch.isPending}>
              {patch.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
